import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { getStorefrontDisabled } from 'helpers/cubohStorefront';
import { useAuth } from 'hooks/useAuth';
import { useMenuManagement } from 'hooks/useMenuManagement';

import { MultiSelect } from '@mantine/core';
import { getIntegrations } from '../helpers';

export default function Integrations({ menu, selectedIntegrations, setSelectedIntegrations }) {
  const { companies, merchants } = useAuth();
  const {
    fetchAllSupportedIntegrations,
    fetchAvailableClassifications,
    fetchMerchantIntegrations,
    getSupportedIntegrationsFromState,
    getMerchantIntegrationsFromState,
  } = useMenuManagement();

  const [defaultIntegrations, setDefaultIntegrations] = useState();

  const isDisabledData = useMemo(
    () =>
      companies[menu?.companyId] &&
      merchants[menu?.merchantId] &&
      getStorefrontDisabled({
        selectedCompany: companies[menu?.companyId],
        merchant: merchants[menu?.merchantId],
      }),
    [menu, companies, merchants],
  );

  const addCubohAsIntegration =
    isDisabledData?.stripeConnected &&
    isDisabledData?.merchantInfoComplete &&
    isDisabledData?.companyInfoComplete;

  useEffect(() => {
    const getOptions = async () => {
      try {
        const menuManagementSupported = getSupportedIntegrationsFromState();
        const activeMerchantIntegrations = getMerchantIntegrationsFromState(
          menu?.merchantId,
          addCubohAsIntegration,
        );

        // filter to only the push integrations
        const menuMgtSupportedIntegrationsObj = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const item of menuManagementSupported) {
          if (item.isSupportedPush) {
            menuMgtSupportedIntegrationsObj[item.integrationName] = item.readableAppName;
          }
        }

        // find the intersection between supportedObj and merchantIntegrations
        const validOptions = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const key in menuMgtSupportedIntegrationsObj) {
          if (activeMerchantIntegrations?.[key]) {
            validOptions.push({
              value: key,
              label: menuMgtSupportedIntegrationsObj[key],
            });
          }
        }
        setDefaultIntegrations(validOptions);
      } catch (e) {
        Sentry.captureException(e);
      }
    };
    getOptions();
  }, [
    menu,
    addCubohAsIntegration,
    getMerchantIntegrationsFromState,
    getSupportedIntegrationsFromState,
  ]);

  useEffect(() => {
    if (menu) {
      fetchAllSupportedIntegrations(menu?.companyId);
      fetchAvailableClassifications(menu?.companyId);
      fetchMerchantIntegrations(menu?.companyId, menu?.merchantId);

      const integrations = getIntegrations(menu);
      setSelectedIntegrations(integrations);
    }
  }, [menu]);

  return (
    <MultiSelect
      maw={600}
      value={selectedIntegrations}
      data={defaultIntegrations}
      onChange={val => setSelectedIntegrations(val)}
      disabled={!defaultIntegrations?.length}
      placeholder={
        !defaultIntegrations?.length
          ? 'There are no options available. Please add options, then try again.'
          : ''
      }
    />
  );
}

Integrations.propTypes = {
  menu: PropTypes.shape({
    companyId: PropTypes.number,
    integrations: PropTypes.arrayOf(
      PropTypes.shape({
        integrationName: PropTypes.string,
        readableAppName: PropTypes.string,
      }),
    ),
    merchantId: PropTypes.number,
  }),
  selectedIntegrations: PropTypes.arrayOf(PropTypes.string),
  setSelectedIntegrations: PropTypes.func,
};
